import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * Injects Google Analytics, Google Tag Manager, and Meta Pixel snippets
 * when their IDs are present in the build env. Re-emits a pageview on
 * every route change so SPA navigation is tracked.
 *
 * IDs are read from VITE_GA_ID / VITE_META_PIXEL_ID / VITE_GTM_ID at
 * build time. Set them in `.env.production` (or a deploy-time secret) and
 * rebuild to roll new IDs.
 */
export function Analytics() {
  const { pathname, search } = useLocation();
  const url = pathname + search;
  const lastUrlRef = useRef(url);

  const ga = import.meta.env.VITE_GA_ID?.trim();
  const pixel = import.meta.env.VITE_META_PIXEL_ID?.trim();
  const gtm = import.meta.env.VITE_GTM_ID?.trim();

  // Re-emit pageview on SPA route changes (the loader snippet only counts the
  // initial page load). We skip the very first render because the snippet
  // already counted it.
  useEffect(() => {
    if (lastUrlRef.current === url) return;
    lastUrlRef.current = url;

    if (ga && typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: url,
      });
    }
    if (pixel && typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
    if (gtm && typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "pageview", page: url });
    }
  }, [url, ga, pixel, gtm]);

  if (!ga && !pixel && !gtm) return null;

  return (
    <Helmet>
      {ga && <script async src={`https://www.googletagmanager.com/gtag/js?id=${ga}`} />}
      {ga && (
        <script>
          {`window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${ga}', { send_page_view: true });`}
        </script>
      )}
      {gtm && (
        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtm}');`}
        </script>
      )}
      {pixel && (
        <script>
          {`!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
fbq('init','${pixel}');
fbq('track','PageView');`}
        </script>
      )}
    </Helmet>
  );
}
